import * as React from "react"
import {
  format,
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  addDays,
  addMonths,
  subMonths,
  isSameMonth,
  isSameDay,
  isToday,
} from "date-fns"
import { ChevronLeft, ChevronRight, Calendar as CalendarIcon } from "lucide-react"

import { cn } from "@/lib/utils"
import { Button } from "@/components/ui/button"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"

interface CalendarProps {
  value?: string | null // YYYY-MM-DD
  onChange: (date: string) => void
  placeholder?: string
  disabled?: boolean
  className?: string
}

const DAY_NAMES = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"]

export function Calendar({
  value,
  onChange,
  placeholder = "Pick a date",
  disabled = false,
  className,
}: CalendarProps) {
  const [open, setOpen] = React.useState(false)
  const [viewDate, setViewDate] = React.useState(() => {
    if (value) return new Date(value + "T00:00:00")
    return new Date()
  })

  // Keep viewDate in sync when value changes externally
  React.useEffect(() => {
    if (value) {
      const d = new Date(value + "T00:00:00")
      if (!isNaN(d.getTime())) {
        setViewDate(d)
      }
    }
  }, [value])

  const selectedDate = React.useMemo(() => {
    if (!value) return null
    const d = new Date(value + "T00:00:00")
    return isNaN(d.getTime()) ? null : d
  }, [value])

  const displayText = React.useMemo(() => {
    if (!selectedDate) return placeholder
    return format(selectedDate, "MMM d, yyyy")
  }, [selectedDate, placeholder])

  const monthStart = startOfMonth(viewDate)
  const monthEnd = endOfMonth(monthStart)
  const calendarStart = startOfWeek(monthStart)
  const calendarEnd = endOfWeek(monthEnd)

  const weeks: Date[][] = React.useMemo(() => {
    const days: Date[] = []
    let day = calendarStart
    while (day <= calendarEnd) {
      days.push(day)
      day = addDays(day, 1)
    }
    const result: Date[][] = []
    for (let i = 0; i < days.length; i += 7) {
      result.push(days.slice(i, i + 7))
    }
    return result
  }, [calendarStart, calendarEnd])

  const handleSelect = (date: Date) => {
    const yyyy = date.getFullYear()
    const mm = String(date.getMonth() + 1).padStart(2, "0")
    const dd = String(date.getDate()).padStart(2, "0")
    onChange(`${yyyy}-${mm}-${dd}`)
    setOpen(false)
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          disabled={disabled}
          className={cn(
            "w-full justify-start text-left font-normal",
            !selectedDate && "text-muted-foreground",
            className
          )}
        >
          <CalendarIcon className="mr-2 h-4 w-4 shrink-0" />
          <span className="truncate">{displayText}</span>
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <div className="p-3 bg-background rounded-md border border-border shadow-lg">
          {/* Header: month/year + nav */}
          <div className="flex items-center justify-between mb-3">
            <Button
              type="button"
              variant="ghost"
              size="icon-sm"
              onClick={() => setViewDate((d) => subMonths(d, 1))}
              className="h-7 w-7"
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <div className="text-sm font-semibold text-foreground">
              {format(viewDate, "MMMM yyyy")}
            </div>
            <Button
              type="button"
              variant="ghost"
              size="icon-sm"
              onClick={() => setViewDate((d) => addMonths(d, 1))}
              className="h-7 w-7"
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>

          {/* Day name headers */}
          <div className="grid grid-cols-7 gap-0.5 mb-1">
            {DAY_NAMES.map((name) => (
              <div
                key={name}
                className="h-7 w-7 flex items-center justify-center text-xs font-medium text-muted-foreground"
              >
                {name}
              </div>
            ))}
          </div>

          {/* Day grid */}
          <div className="space-y-0.5">
            {weeks.map((week, wi) => (
              <div key={wi} className="grid grid-cols-7 gap-0.5">
                {week.map((day, di) => {
                  const inMonth = isSameMonth(day, viewDate)
                  const selected = selectedDate ? isSameDay(day, selectedDate) : false
                  const today = isToday(day)

                  return (
                    <Button
                      key={di}
                      type="button"
                      variant={selected ? "default" : "ghost"}
                      size="icon-sm"
                      onClick={() => handleSelect(day)}
                      disabled={!inMonth}
                      className={cn(
                        "h-7 w-7 text-xs",
                        !inMonth && "invisible",
                        !selected && today && "border border-primary text-primary",
                        !selected && !today && inMonth && "text-foreground hover:bg-accent",
                      )}
                    >
                      {format(day, "d")}
                    </Button>
                  )
                })}
              </div>
            ))}
          </div>
        </div>
      </PopoverContent>
    </Popover>
  )
}
